/**
 * Copyright (c) 2026 Diversabilities / Queensland Compassionate Services.
 * Licensed under the ISC License.
 *
 * AI Global Search — header live-search dropdown.
 *
 * Hooks into the EXISTING header search input (#site-header-search-input /
 * #site-header-search-form, see template-parts/header/header-navigation.php)
 * — no new markup is added to the template. This script only creates the
 * results dropdown element itself, at runtime, and appends it next to the
 * existing form.
 *
 * Calls the WP REST proxy (inc/ai-search/ai-search-service.php), not the
 * Django host directly, so the upstream URL stays server-side/configurable.
 *
 * v1 is guest-only: no Authorization/JWT, only a client-generated
 * guest_session_id persisted in localStorage (the doc's own reference
 * pattern), consistent with this being the theme's first localStorage use.
 */
document.addEventListener('DOMContentLoaded', () => {
  const searchInput = document.getElementById('site-header-search-input');
  const searchForm = document.getElementById('site-header-search-form');
  const searchToggle = document.getElementById('site-header-search');

  if (!searchInput || !searchForm) {
    return;
  }

  const MIN_QUERY_LENGTH = 2;
  const DEBOUNCE_MS = 300;
  const RADIX_BASE_36 = 36;
  const GUEST_ID_STORAGE_KEY = 'ai_search_guest_id';
  const ERROR_CLASS_NAME = 'ai-search-error';
  const GENERIC_ERROR_MESSAGE = 'Unable to complete search. Please try again.';

  let debounceTimer = null;
  let activeController = null;
  let latestRequestId = 0;

  const RTL_LANGS = ['ar'];

  /* ================== RESULTS CONTAINER ================== */
  const resultsContainer = document.createElement('div');
  resultsContainer.className = 'ai-search-results-dropdown';
  resultsContainer.id = 'ai-search-results';
  resultsContainer.style.display = 'none';
  resultsContainer.setAttribute('dir', 'ltr');
  searchForm.insertAdjacentElement('afterend', resultsContainer);

  const I18N_STRINGS = {
    en: {
      loading: 'Searching...',
      emptyTitle: 'No results found',
      emptySub: 'Try adjusting your search to find what you are looking for.',
      unavailable: 'Search is not available right now.',
      genericError: 'Unable to complete search. Please try again.',
      showingResultsFor: 'Showing results for:',
    },
    zh: {
      loading: '正在搜索...',
      emptyTitle: '未找到结果',
      emptySub: '请尝试调整您的搜索内容以查找您需要的信息。',
      unavailable: '当前搜索服务不可用。',
      genericError: '无法完成搜索，请重试。',
      showingResultsFor: '显示以下内容的结果：',
    },
    ar: {
      loading: 'جارٍ البحث...',
      emptyTitle: 'لم يتم العثور على نتائج',
      emptySub: 'حاول تعديل بحثك للعثور على ما تبحث عنه.',
      unavailable: 'البحث غير متاح حالياً.',
      genericError: 'تعذر إكمال البحث. يُرجى المحاولة مرة أخرى.',
      showingResultsFor: 'عرض النتائج لـ:',
    },
    vi: {
      loading: 'Đang tìm kiếm...',
      emptyTitle: 'Không tìm thấy kết quả',
      emptySub: 'Hãy thử điều chỉnh tìm kiếm của bạn để tìm thấy thông tin cần thiết.',
      unavailable: 'Tìm kiếm hiện không khả dụng.',
      genericError: 'Không thể hoàn tất tìm kiếm. Vui lòng thử lại.',
      showingResultsFor: 'Hiển thị kết quả cho:',
    },
    pa: {
      loading: 'ਖੋਜ ਜਾਰੀ ਹੈ...',
      emptyTitle: 'ਕੋਈ ਨਤੀਜਾ ਨਹੀਂ ਮਿਲਿਆ',
      emptySub: 'ਤੁਸੀਂ ਜੋ ਲੱਭ ਰਹੇ ਹੋ ਉਸਨੂੰ ਲੱਭਣ ਲਈ ਆਪਣੀ ਖੋਜ ਨੂੰ ਬਦਲਣ ਦੀ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
      unavailable: 'ਖੋਜ ਫਿਲਹਾਲ ਉਪਲਬਧ ਨਹੀਂ ਹੈ।',
      genericError: 'ਖੋਜ ਪੂਰੀ ਕਰਨ ਵਿੱਚ ਅਸਮਰੱਥ। ਕਿਰਪਾ ਕਰਕੇ ਦੁਬਾਰਾ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
      showingResultsFor: 'ਲਈ ਨਤੀਜੇ ਦਿਖਾ ਰਿਹਾ ਹੈ:',
    },
  };

  const getI18nText = (key, lang) => {
    if (I18N_STRINGS[lang]?.[key]) {
      return I18N_STRINGS[lang][key];
    }
    if (I18N_STRINGS.en?.[key]) {
      return I18N_STRINGS.en[key];
    }
    return '';
  };

  // Resolved from active globals, HTML attribute, cookie, or URL path
  const getCurrentLang = () => {
    if (window.dvTranslation?.currentLang && window.dvTranslation.currentLang !== 'en') {
      return window.dvTranslation.currentLang;
    }
    if (window.dvAiSearch?.currentLang && window.dvAiSearch.currentLang !== 'en') {
      return window.dvAiSearch.currentLang;
    }
    const docLang = document.documentElement.getAttribute('lang');
    if (docLang && ['zh', 'ar', 'vi', 'pa'].indexOf(docLang) !== -1) {
      return docLang;
    }
    const cookieRegex = /(?:^|;\s*)diversability_lang=([^;]+)/;
    const cookieMatch = cookieRegex.exec(document.cookie);
    if (cookieMatch?.[1]) {
      const cookieLang = decodeURIComponent(cookieMatch[1]).trim();
      if (['zh', 'ar', 'vi', 'pa', 'en'].indexOf(cookieLang) !== -1) {
        return cookieLang;
      }
    }
    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    if (pathSegments.length > 0 && ['zh', 'ar', 'vi', 'pa'].indexOf(pathSegments[0]) !== -1) {
      return pathSegments[0];
    }
    if (window.dvTranslation?.currentLang) {
      return window.dvTranslation.currentLang;
    }
    if (window.dvAiSearch?.currentLang) {
      return window.dvAiSearch.currentLang;
    }
    return 'en';
  };

  const hideResults = () => {
    resultsContainer.style.display = 'none';
    resultsContainer.innerHTML = '';
  };

  const showResults = () => {
    resultsContainer.style.display = 'block';
  };

  const renderMessage = (className, message) => {
    resultsContainer.innerHTML = `<div class="${className}">${escapeHtml(message)}</div>`;
  };

  /* ================== GUEST SESSION ID ================== */
  const generateGuestSessionId = () => {
    if (window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }
    return `web_guest_${Math.random().toString(RADIX_BASE_36).slice(2)}_${new Date().getTime()}`;
  };

  const getOrCreateGuestSessionId = () => {
    try {
      const existing = window.localStorage.getItem(GUEST_ID_STORAGE_KEY);
      if (existing) {
        return existing;
      }

      const generated = generateGuestSessionId();
      window.localStorage.setItem(GUEST_ID_STORAGE_KEY, generated);
      return generated;
    } catch (err) {
      // localStorage unavailable (private browsing, storage quota, etc.) —
      // fall back to a per-page-load id rather than failing search entirely.
      // Rule S106: console.info is allowed by Sonar rules
      console.info('[AI Search] Local storage inaccessible, using transient guest ID:', err);
      return generateGuestSessionId();
    }
  };

  /* ================== DEBOUNCE ================== */
  const scheduleSearch = queryText => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => performAISearch(queryText), DEBOUNCE_MS);
  };

  /* ================== SEARCH ================== */
  const buildSearchUrl = (restUrl, trimmedQuery, lang) => {
    const params = new URLSearchParams({
      query: trimmedQuery,
      language: lang,
      page: '1',
      page_size: '10',
      guest_session_id: getOrCreateGuestSessionId(),
    });

    return `${restUrl}?${params.toString()}`;
  };

  const handleSearchResponse = (json, requestId, lang) => {
    if (requestId !== latestRequestId) {
      return; // a newer request has since started
    }

    if (json?.status && json?.data) {
      renderSearchResults(json.data, lang);
    } else {
      renderMessage(ERROR_CLASS_NAME, json?.message || getI18nText('genericError', lang));
    }
  };

  function performAISearch(queryText) {
    const trimmedQuery = (queryText || '').trim();

    if (trimmedQuery.length < MIN_QUERY_LENGTH) {
      hideResults();
      return;
    }

    if (activeController) {
      activeController.abort();
    }

    // Rule S1774: Avoid ternary operator
    if (window.AbortController) {
      activeController = new AbortController();
    } else {
      activeController = null;
    }

    // Rule S881: Dedicated increment statement
    latestRequestId += 1;
    const requestId = latestRequestId;

    const lang = getCurrentLang();

    // Rule S1774: Avoid ternary operator
    if (RTL_LANGS.indexOf(lang) !== -1) {
      resultsContainer.setAttribute('dir', 'rtl');
    } else {
      resultsContainer.setAttribute('dir', 'ltr');
    }

    showResults();
    renderMessage('ai-search-loading', getI18nText('loading', lang));

    const restUrl = window.dvAiSearch?.restUrl || '';
    if (!restUrl) {
      renderMessage(ERROR_CLASS_NAME, getI18nText('unavailable', lang));
      return;
    }

    fetch(buildSearchUrl(restUrl, trimmedQuery, lang), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: activeController?.signal,
    })
      .then(response => response.json())
      .then(json => handleSearchResponse(json, requestId, lang))
      .catch(err => {
        if (err?.name === 'AbortError') {
          return;
        }
        if (requestId !== latestRequestId) {
          return;
        }
        renderMessage(ERROR_CLASS_NAME, getI18nText('genericError', lang));
      });
  }

  /* ================== RENDER ================== */
  const ARROW_ICON_SVG =
    '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<rect x="0.75" y="0.75" width="22.5" height="22.5" rx="7.25" stroke="currentColor" stroke-width="1.5"/>' +
    '<path d="M9 15L15 9M15 9H10M15 9V14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  const isImageFile = url => {
    if (!url || typeof url !== 'string') return false;
    if (/\.(mp4|mov|webm|avi|mkv|m4v|mp3|wav|ogg)(\?.*)?$/i.test(url)) return false;
    return true;
  };

  const renderThumb = item => {
    const initial = (item.title || '?').trim().charAt(0).toUpperCase() || '?';
    if (item.thumbnail_url && isImageFile(item.thumbnail_url)) {
      return `<img src="${escapeHtml(item.thumbnail_url)}" alt="" loading="lazy" onerror="this.onerror=null;this.style.display='none';if(this.nextElementSibling){this.nextElementSibling.style.display='flex';}">` +
        `<span class="ai-search-thumb-fallback" style="display:none">${escapeHtml(initial)}</span>`;
    }
    return `<span class="ai-search-thumb-fallback">${escapeHtml(initial)}</span>`;
  };

  const renderResultRow = (item, homeUrl) => {
    const subtitle = item.subtitle || item.content_type_label || '';
    let rawPath = item.url_path || item.permalink || '';

    if (rawPath.startsWith('http://') || rawPath.startsWith('https://')) {
      try {
        const u = new URL(rawPath);
        u.pathname = u.pathname
          .replace(/\/+/g, '/')
          .replace(/\/(videos|useful-links|podcasts|library|events|event|services|service|service-provider)\/\1\//gi, '/$1/')
          .replace(/(^|\/)event\/(?=[^\/]+)/gi, '$1events/');
        rawPath = u.toString();
      } catch (e) {
        // Fall back to original rawPath
      }
      return `<a href="${escapeHtml(rawPath)}" class="ai-search-item-row">` +
        `<div class="ai-search-thumb-box">${renderThumb(item)}</div>` +
        `<div class="ai-search-content-box">` +
        `<span class="ai-search-subtitle">${escapeHtml(subtitle)}</span>` +
        `<h5 class="ai-search-title">${escapeHtml(item.title)}</h5>` +
        `</div>` +
        `<span class="ai-search-arrow-icon" aria-hidden="true">${ARROW_ICON_SVG}</span></a>`;
    }

    // Relative path normalization
    rawPath = rawPath
      .replace(/\/+/g, '/')
      .replace(/\/(videos|useful-links|podcasts|library|events|event|services|service|service-provider)\/\1\//gi, '/$1/')
      .replace(/(^|\/)event\/(?=[^\/]+)/gi, '$1events/');

    if (/^\/?(videos|useful-links|podcasts|library)\//i.test(rawPath)) {
      rawPath = rawPath.replace(/^\/?(videos|useful-links|podcasts|library)\//i, 'resources/$1/');
    }

    const currentLang = window.dvAiSearch?.currentLang || 'en';
    let cleanRel = rawPath.replace(/^\//, '');
    if (currentLang && !cleanRel.startsWith(currentLang + '/')) {
      cleanRel = `${currentLang}/${cleanRel}`;
    }

    const href = `${homeUrl.replace(/\/$/, '')}/${cleanRel}`;


    return `<a href="${escapeHtml(href)}" class="ai-search-item-row">` +
      `<div class="ai-search-thumb-box">${renderThumb(item)}</div>` +
      `<div class="ai-search-content-box">` +
      `<span class="ai-search-subtitle">${escapeHtml(subtitle)}</span>` +
      `<h5 class="ai-search-title">${escapeHtml(item.title)}</h5>` +
      `</div>` +
      `<span class="ai-search-arrow-icon" aria-hidden="true">${ARROW_ICON_SVG}</span></a>`;
  };

  const renderSection = (sectionName, items, homeUrl) => {
    const rows = items.map(item => renderResultRow(item, homeUrl)).join('');
    return `<div class="ai-search-section">` +
      `<h4 class="ai-search-section-header">${escapeHtml(sectionName)}</h4>` +
      `<div class="ai-search-items-list">${rows}</div>` +
      `</div>`;
  };

  function renderSearchResults(data, lang) {
    if (!data.total_records) {
      resultsContainer.innerHTML =
        '<div class="ai-search-empty-state">' +
        `<div class="ai-search-empty-title">${escapeHtml(getI18nText('emptyTitle', lang))}</div>` +
        `<div class="ai-search-empty-sub">${escapeHtml(getI18nText('emptySub', lang))}</div>` +
        '</div>';
      return;
    }

    const grouped = data.grouped_results || {};
    const homeUrl = window.dvAiSearch?.homeUrl || '/';

    const sections = Object.keys(grouped)
      .map(sectionName => renderSection(sectionName, grouped[sectionName] || [], homeUrl))
      .join('');

    let autocorrectHtml = '';
    if (data.corrected_query && data.corrected_query.toLowerCase() !== (data.query || '').toLowerCase()) {
      autocorrectHtml =
        `<div class="ai-search-autocorrect-banner" style="padding: 8px 12px; margin-bottom: 12px; font-size: 13px; background: #f8f9fa; border-radius: 8px; border: 1px solid #e9ecef;">` +
        `<span>${escapeHtml(getI18nText('showingResultsFor', lang) || 'Showing results for:')} </span>` +
        `<strong>${escapeHtml(data.corrected_query)}</strong>` +
        `</div>`;
    }

    resultsContainer.innerHTML = `${autocorrectHtml}<div class="ai-search-grouped-container">${sections}</div>`;
  }

  function escapeHtml(text) {
    if (!text) {
      return '';
    }
    const escapeMap = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
    return String(text).replace(/[&<>"']/g, m => escapeMap[m]);
  }

  /* ================== EVENTS ================== */
  searchInput.addEventListener('input', e => scheduleSearch(e.target.value));

  if (searchToggle) {
    searchToggle.addEventListener('click', () => hideResults());
  }

  window.addEventListener('diversability-language-changed', e => {
    const newLang = e?.detail?.lang;
    if (newLang) {
      if (window.dvAiSearch) {
        window.dvAiSearch.currentLang = newLang;
      }
      if (window.dvTranslation) {
        window.dvTranslation.currentLang = newLang;
      }
      if (resultsContainer.style.display !== 'none' && searchInput.value.trim().length >= MIN_QUERY_LENGTH) {
        performAISearch(searchInput.value);
      }
    }
  });

  document.addEventListener('click', e => {
    if (!searchForm.contains(e.target) && !resultsContainer.contains(e.target)) {
      hideResults();
    }
  });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') {
      hideResults();
    }
  });
});
